import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { telemetryService } from '@/services/api/telemetry.service';
import { ApiError } from '@/services/api/client';
import type { CrashGroupQuery, CrashListQuery, LogListQuery } from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for the dashboard, the log viewer and crash triage.
 *
 * All three are read-only, so there are no mutations here and nothing to
 * invalidate. What they do share is a refusal to retry a 403: this surface is
 * IP-allowlisted and role-gated, and hammering a refusal neither fixes it nor
 * tells the person anything they can act on.
 */
const retryUnlessRefused = (failureCount: number, error: Error): boolean => {
  /*
   * Typed as Error, not unknown. React Query infers the query's error type from
   * this callback, and `unknown` here quietly makes `query.error` unknown at
   * every call site - so a component cannot read `.message` off it.
   */
  if (error instanceof ApiError && (error.status === 403 || error.isAuthFailure)) return false;
  return failureCount < 2;
};

export function useDashboard(windowHours?: number) {
  return useQuery({
    queryKey: queryKeys.dashboard.summary(windowHours),
    queryFn: ({ signal }) =>
      telemetryService.dashboard(windowHours === undefined ? {} : { windowHours }, signal),
    /*
     * Every 30 seconds, the design's poll for the KPI cards: someone watching a
     * rollout sees a crash spike in the cards before anywhere else.
     */
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    retry: retryUnlessRefused,
  });
}

export function useLogs(query: LogListQuery) {
  return useQuery({
    queryKey: queryKeys.logs.list(query),
    queryFn: ({ signal }) => telemetryService.logs(query, signal),
    /*
     * Keeps the previous page on screen while the next one loads. Without it
     * the table empties on every filter change, which reads as "no results"
     * for the moment it takes to answer.
     */
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
    retry: retryUnlessRefused,
  });
}

export function useCrashes(query: CrashListQuery) {
  return useQuery({
    queryKey: queryKeys.crashes.list(query),
    queryFn: ({ signal }) => telemetryService.crashes(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    retry: retryUnlessRefused,
  });
}

export function useCrashGroups(query: CrashGroupQuery) {
  return useQuery({
    queryKey: queryKeys.crashes.groups(query),
    queryFn: ({ signal }) => telemetryService.crashGroups(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    retry: retryUnlessRefused,
  });
}
